import {
	deletePushNotificationRegistrations,
	PushNotificationCallbacks,
	setupFirebaseMessaging,
} from "./firebaseMessaging";
import { useAsync } from "react-async-hook";
import { getAndResetInitialNotificationAction } from "./initialNotificationAction";
import { makeAxiosRequest } from "../api/utils/apiClient";
import React from "react";
import { PushNotificationsContext } from "./PushNotificationsContext";
import type { PushNotificationClickAction } from "src/notifications/pushNotificationClickAction.ts";
import { isCustomAppWindowPath } from "src/customApps/runtime/customAppWindows.ts";

type PushNotificationsProviderProps = {
	children: React.ReactNode;
};

export function PushNotificationsContextProvider({ children }: PushNotificationsProviderProps) {
	const [registrationToken, setRegistrationToken] = React.useState<string | null>(null);

	const [clickAction, setClickAction] = React.useState<PushNotificationClickAction | null>(null);

	const pushNotificationCallbacks: PushNotificationCallbacks = {
		clickActionReceived: setClickAction,
	};

	useAsync(async () => {
		// A Custom App popup may be on a customer-facing display, so it must not ask for notification permission.
		if (isCustomAppWindowPath(window.location.pathname)) return;
		await setupMessaging(pushNotificationCallbacks, setRegistrationToken);
		handlePossibleInitialMessageAction(setClickAction);
	}, []);

	const contextValue = {
		registrationToken,
		clickAction,
		onClickActionHandled: (actionId: string) => {
			setClickAction((current) => (current?.actionId === actionId ? null : current));
		},
		deleteRegistration: async () => {
			setRegistrationToken(null);
			await deletePushNotificationRegistrations();
		},
	};

	return <PushNotificationsContext.Provider value={contextValue}>{children}</PushNotificationsContext.Provider>;
}

async function setupMessaging(
	pushNotificationCallbacks: PushNotificationCallbacks,
	setRegistrationToken: (value: string | null) => void,
) {
	try {
		const token = await setupFirebaseMessaging(pushNotificationCallbacks);
		if (token == null) {
			console.info("Null push notification token returned");
			return;
		}
		setRegistrationToken(token);
		await sendRegistrationTokenToServer(token);
	} catch (error) {
		console.error("Push notification token fetching failed", error);
	}
}

async function sendRegistrationTokenToServer(token: string) {
	await makeAxiosRequest({
		url: "/api/notifications/subscribe",
		method: "POST",
		data: {
			registrationToken: token,
		},
	});
}

function handlePossibleInitialMessageAction(setClickAction: (action: PushNotificationClickAction) => void) {
	const initialActionEvent = getAndResetInitialNotificationAction();
	if (initialActionEvent) setClickAction(initialActionEvent);
}
